import { Download, Eye, Play, RefreshCw, Trash2 } from 'lucide-react';
import { LiksIconButton } from '../../components/liks/LiksIconButton/LiksIconButton';
import { LiksTooltip } from '../../components/liks/LiksTooltip/LiksTooltip';
import {
  canResumeAbandonedDocument,
  canRetryFailedUploadDocument,
  getDocumentLabel,
} from '../../features/knowledge/documentActionEligibility';
import type { KnowledgeBaseDocument } from '../../types/knowledge';
import type { WithResourceId } from '../../types/knowledge';

type DocumentRow = WithResourceId<KnowledgeBaseDocument>;

type KnowledgeDocumentTableActionsProps = {
  mode: 'row' | 'bulk';
  documents: DocumentRow[];
  canManageDocuments: boolean;
  disabled?: boolean;
  translate: (key: string, params?: Record<string, string>) => string;
  onPreview: (documents: DocumentRow[]) => void;
  onDownload: (documents: DocumentRow[]) => void;
  onResume: (documents: DocumentRow[]) => void;
  onRetry: (documents: DocumentRow[]) => void;
  onDelete: (documents: DocumentRow[]) => void;
};

export function KnowledgeDocumentTableActions({
  mode,
  documents,
  canManageDocuments,
  disabled = false,
  translate,
  onPreview,
  onDownload,
  onResume,
  onRetry,
  onDelete,
}: KnowledgeDocumentTableActionsProps) {
  if (documents.length === 0) {
    return null;
  }

  const isBulk = mode === 'bulk';
  const single = documents.length === 1 ? documents[0] : null;
  const singleLabel = single ? getDocumentLabel(single) : '';

  const canResume = canManageDocuments
    ? isBulk
      ? documents.every(canResumeAbandonedDocument)
      : single != null && canResumeAbandonedDocument(single)
    : false;
  const canRetry = canManageDocuments
    ? isBulk
      ? documents.every(canRetryFailedUploadDocument)
      : single != null && canRetryFailedUploadDocument(single)
    : false;
  const canDelete = canManageDocuments;

  return (
    <div className="knowledge-database__resource-actions">
      {canResume ? (
        <LiksTooltip
          content={translate(
            isBulk
              ? 'knowledge.documentResumeSelected'
              : 'knowledge.documentResume',
          )}
          placement="top-end"
        >
          <LiksIconButton
            className="knowledge-database__action-button knowledge-database__action-button--resume"
            aria-label={
              isBulk
                ? translate('knowledge.documentResumeSelectedCount', {
                    count: String(documents.length),
                  })
                : translate('knowledge.documentResumeDocument', {
                    fileName: singleLabel,
                  })
            }
            disabled={disabled}
            onClick={() => onResume(documents)}
          >
            <Play aria-hidden />
          </LiksIconButton>
        </LiksTooltip>
      ) : null}

      {canRetry ? (
        <LiksTooltip
          content={translate(
            isBulk
              ? 'knowledge.documentRetrySelected'
              : 'knowledge.documentRetry',
          )}
          placement="top-end"
        >
          <LiksIconButton
            className="knowledge-database__action-button knowledge-database__action-button--retry"
            aria-label={
              isBulk
                ? translate('knowledge.documentRetrySelectedCount', {
                    count: String(documents.length),
                  })
                : translate('knowledge.documentRetryDocument', {
                    fileName: singleLabel,
                  })
            }
            disabled={disabled}
            onClick={() => onRetry(documents)}
          >
            <RefreshCw aria-hidden />
          </LiksIconButton>
        </LiksTooltip>
      ) : null}

      <LiksTooltip
        content={translate(
          isBulk ? 'knowledge.previewSelected' : 'knowledge.preview',
        )}
        placement="top-end"
      >
        <LiksIconButton
          className="knowledge-database__action-button knowledge-database__action-button--preview"
          aria-label={
            isBulk
              ? translate('knowledge.previewSelectedCount', {
                  count: String(documents.length),
                })
              : translate('knowledge.previewDocument', {
                  fileName: singleLabel,
                })
          }
          disabled={disabled}
          onClick={() => onPreview(documents)}
        >
          <Eye aria-hidden />
        </LiksIconButton>
      </LiksTooltip>

      <LiksTooltip
        content={translate(
          isBulk ? 'knowledge.downloadSelected' : 'knowledge.download',
        )}
        placement="top-end"
      >
        <LiksIconButton
          className="knowledge-database__action-button knowledge-database__action-button--download"
          aria-label={
            isBulk
              ? translate('knowledge.downloadSelectedCount', {
                  count: String(documents.length),
                })
              : translate('knowledge.downloadDocument', {
                  fileName: singleLabel,
                })
          }
          disabled={disabled}
          onClick={() => onDownload(documents)}
        >
          <Download aria-hidden />
        </LiksIconButton>
      </LiksTooltip>

      {canDelete ? (
        <LiksTooltip
          content={translate(
            isBulk ? 'knowledge.deleteSelected' : 'knowledge.delete',
          )}
          placement="top-end"
        >
          <LiksIconButton
            className="knowledge-database__action-button knowledge-database__action-button--delete"
            aria-label={
              isBulk
                ? translate('knowledge.deleteSelectedCount', {
                    count: String(documents.length),
                  })
                : translate('knowledge.deleteDocument', {
                    fileName: singleLabel,
                  })
            }
            disabled={disabled}
            onClick={() => onDelete(documents)}
          >
            <Trash2 aria-hidden />
          </LiksIconButton>
        </LiksTooltip>
      ) : null}
    </div>
  );
}
